import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

// Animation timeline (ms from start)
const STEP_TIMINGS = {
  card: 400,       // Lead card frame appears
  field1: 1200,    // Case type field fills
  field2: 2000,    // Injury field fills
  field3: 2800,    // Treatment field fills
  field4: 3600,    // Fault field fills
  score: 4600,     // Lead score meter fills + badge
  booked: 6200,    // Booked row appears
  restart: 10500,  // Loop back to start
};

// One lead per loop cycle — each from a different practice area AND a
// different channel, so the card quietly demonstrates the breadth of firms
// served alongside intake, speed to lead, recovery, and multi-channel.
const leadScenarios = [
  {
    name: 'Michael T.',
    channel: 'Inbound call · 8:42 PM',
    score: 92,
    fields: [
      { label: 'Case type', value: 'Car accident', step: 1 },
      { label: 'Injuries reported', value: 'Yes — hospitalized', step: 2 },
      { label: 'Receiving treatment', value: 'Yes — ongoing', step: 3 },
      { label: 'Fault', value: 'Other driver cited', step: 4 },
    ],
    booked: 'Consultation · Tomorrow at 10:00 AM',
  },
  {
    name: 'Sarah K.',
    channel: 'Web form · called back in 9s',
    score: 88,
    fields: [
      { label: 'Case type', value: 'Criminal defense — DUI', step: 1 },
      { label: 'Court date', value: 'In 12 days', step: 2 },
      { label: 'Prior record', value: 'None — first offense', step: 3 },
      { label: 'Currently in custody', value: 'No — released', step: 4 },
    ],
    booked: 'Consultation · Thursday at 2:30 PM',
  },
  {
    name: 'Danielle R.',
    channel: 'Missed call · recovered by text',
    score: 90,
    fields: [
      { label: 'Case type', value: 'Wrongful termination', step: 1 },
      { label: 'Employer size', value: '200+ employees', step: 2 },
      { label: 'Reason suspected', value: 'Reported harassment', step: 3 },
      { label: 'Documentation', value: 'Yes — emails saved', step: 4 },
    ],
    booked: 'Consultation · Friday at 11:15 AM',
  },
  {
    name: 'Luis G.',
    channel: 'WhatsApp · 11:18 PM',
    score: 86,
    fields: [
      { label: 'Case type', value: 'SSDI — denied claim', step: 1 },
      { label: 'Denial date', value: '3 weeks ago', step: 2 },
      { label: 'Condition', value: 'Back injury — chronic', step: 3 },
      { label: 'Under a doctor’s care', value: 'Yes — ongoing', step: 4 },
    ],
    booked: 'Consultation · Monday at 9:00 AM',
  },
  {
    name: 'Patricia H.',
    channel: 'Web chat · 6:05 PM',
    score: 94,
    fields: [
      { label: 'Case type', value: 'Mass tort — talc', step: 1 },
      { label: 'Diagnosis', value: 'Ovarian cancer, 2024', step: 2 },
      { label: 'Product use', value: '10+ years — regular', step: 3 },
      { label: 'Medical records', value: 'Yes — available', step: 4 },
    ],
    booked: 'Case review · Wednesday at 3:00 PM',
  },
];

function QualificationField({
  label,
  value,
  visible,
}: {
  label: string;
  value: string;
  visible: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        py: 0.75,
        borderBottom: '1px solid #F0EAE0',
      }}
    >
      <Typography sx={{ fontSize: '0.72rem', color: '#6B5F4D', flexShrink: 0 }}>
        {label}
      </Typography>
      {visible ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            animation: 'fieldIn 0.35s ease',
            '@keyframes fieldIn': {
              '0%': { opacity: 0, transform: 'translateX(8px)' },
              '100%': { opacity: 1, transform: 'translateX(0)' },
            },
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 13, color: '#27AE60' }} />
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 600,
              color: '#3E301E',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </Typography>
        </Box>
      ) : (
        // Skeleton placeholder while the AI is still asking
        <Box sx={{ height: 10, width: 72, backgroundColor: '#F0EAE0', borderRadius: 1 }} />
      )}
    </Box>
  );
}

export default function QualificationAnimation() {
  const [step, setStep] = React.useState(0);
  const [cycle, setCycle] = React.useState(0);

  // Steps: 1-4=fields fill in, 5=lead score, 6=booked
  React.useEffect(() => {
    const timers = [
      setTimeout(() => setStep(0.5), STEP_TIMINGS.card),
      setTimeout(() => setStep(1), STEP_TIMINGS.field1),
      setTimeout(() => setStep(2), STEP_TIMINGS.field2),
      setTimeout(() => setStep(3), STEP_TIMINGS.field3),
      setTimeout(() => setStep(4), STEP_TIMINGS.field4),
      setTimeout(() => setStep(5), STEP_TIMINGS.score),
      setTimeout(() => setStep(6), STEP_TIMINGS.booked),
      setTimeout(() => {
        setStep(0);
        setCycle((c) => c + 1);
      }, STEP_TIMINGS.restart),
    ];
    return () => timers.forEach(clearTimeout);
  }, [cycle]);

  const cardVisible = step >= 0.5;
  const scored = step >= 5;
  const lead = leadScenarios[cycle % leadScenarios.length];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minWidth: 0,
        backgroundColor: '#FDFCF9',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1.25,
        px: { xs: 2.5, md: 4 },
        py: 2,
      }}
    >
      {/* Lead info card */}
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: 2,
          border: '1px solid #E8E2D8',
          boxShadow: '0 2px 8px rgba(62, 48, 30, 0.06)',
          px: 2,
          py: 1.5,
          opacity: cardVisible ? 1 : 0,
          transform: cardVisible ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
        }}
      >
        {/* Header: caller identity + criteria badge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: 'rgba(181, 104, 60, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <PersonIcon sx={{ fontSize: 20, color: '#B5683C' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#3E301E', lineHeight: 1.3 }}>
              {lead.name}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#6B5F4D' }}>
              {lead.channel}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1.25,
              py: 0.4,
              borderRadius: 2,
              backgroundColor: 'rgba(39, 174, 96, 0.12)',
              border: '1px solid rgba(39, 174, 96, 0.3)',
              opacity: scored ? 1 : 0,
              transform: scored ? 'scale(1)' : 'scale(0.8)',
              transition: 'all 0.35s ease',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: '#27AE60',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Meets criteria
            </Typography>
          </Box>
        </Box>

        {/* Qualification fields */}
        {lead.fields.map((field) => (
          <QualificationField
            key={field.label}
            label={field.label}
            value={field.value}
            visible={step >= field.step}
          />
        ))}

        {/* Lead score meter */}
        <Box sx={{ pt: 1.25 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#3E301E' }}>
              Lead score
            </Typography>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                color: scored ? '#27AE60' : '#6B5F4D',
                transition: 'color 0.3s ease',
              }}
            >
              {scored ? `${lead.score} / 100` : '— / 100'}
            </Typography>
          </Box>
          <Box
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#F0EAE0',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: scored ? `${lead.score}%` : '0%',
                borderRadius: 4,
                background: 'linear-gradient(90deg, #B5683C 0%, #27AE60 100%)',
                transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Booked row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.75,
          py: 1.25,
          borderRadius: 2,
          backgroundColor: 'rgba(39, 174, 96, 0.08)',
          border: '1px solid rgba(39, 174, 96, 0.3)',
          opacity: step >= 6 ? 1 : 0,
          transform: step >= 6 ? 'translateY(0)' : 'translateY(12px)',
          transition: 'opacity 0.4s ease, transform 0.4s ease',
        }}
      >
        <EventAvailableIcon sx={{ fontSize: 22, color: '#27AE60', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#27AE60', lineHeight: 1.3 }}>
            Booked on your calendar
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#6B5F4D' }}>
            {lead.booked}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
